import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CATEGORIES } from "../../data/menu";
import { Reveal } from "./Reveal";
import { Plus } from "lucide-react";

export const MenuShowcase = () => {
  const [active, setActive] = useState(0);
  const cat = CATEGORIES[active];

  return (
    <section id="menu" data-testid="menu-section" className="relative py-20 md:py-32">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <Reveal className="mb-12 md:mb-16 max-w-3xl">
          <p className="text-xs tracking-[0.3em] uppercase font-medium text-[#B07D4E] mb-5">The Collection</p>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] text-[#2A221C]">
            Seven ranges,<br /><span className="italic text-[#D4A373]">one obsession.</span>
          </h2>
        </Reveal>

        {/* Mobile: horizontal scrollable pills */}
        <div className="md:hidden -mx-6 px-6 mb-8 flex gap-3 overflow-x-auto no-scrollbar pb-2" data-testid="menu-pills">
          {CATEGORIES.map((c, i) => (
            <button
              key={c.id}
              data-testid={`menu-pill-${c.id}`}
              onClick={() => setActive(i)}
              className={`shrink-0 rounded-full border px-5 py-2.5 text-sm transition-colors ${
                i === active
                  ? "bg-[#2A221C] text-[#FDFBF7] border-[#2A221C]"
                  : "bg-transparent text-[#2A221C] border-[#E6E2D8]"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          {/* Desktop sticky tabs */}
          <div className="hidden md:block md:col-span-4">
            <div className="sticky top-28 flex flex-col" data-testid="menu-tabs">
              {CATEGORIES.map((c, i) => (
                <button
                  key={c.id}
                  data-testid={`menu-tab-${c.id}`}
                  onClick={() => setActive(i)}
                  className="group relative text-left py-5 border-b border-[#E6E2D8] flex items-baseline gap-4"
                >
                  <span className={`text-xs tracking-widest transition-colors ${i === active ? "text-[#D4A373]" : "text-[#2A221C]/40"}`}>
                    {c.index}
                  </span>
                  <span
                    className={`font-display text-2xl lg:text-3xl leading-none transition-all duration-300 ${
                      i === active ? "text-[#2A221C] translate-x-1" : "text-[#2A221C]/45 group-hover:text-[#2A221C]/80"
                    }`}
                  >
                    {c.name}
                  </span>
                  {i === active && (
                    <motion.span layoutId="tab-dot" className="absolute right-0 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-[#D4A373]" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content panel */}
          <div className="md:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={cat.id}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`menu-panel-${cat.id}`}
              >
                {/* Clipped spotlight image */}
                <div className="group relative overflow-hidden rounded-sm border border-[#E6E2D8] aspect-[16/10] md:aspect-[16/9]">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#D4A373]/25 opacity-100 group-hover:opacity-0 transition-opacity duration-700" />
                  <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 bg-gradient-to-t from-[#2A221C]/70 to-transparent">
                    <span className="text-[10px] tracking-[0.25em] uppercase text-[#FDFBF7]/90">{cat.format}</span>
                    <p className="font-display italic text-2xl md:text-3xl text-[#FDFBF7] mt-1">{cat.tagline}</p>
                  </div>
                </div>

                {/* Product items */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1">
                  {cat.items.map((item, idx) => (
                    <ItemRow key={item.name} item={item} idx={idx} catId={cat.id} />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

const ItemRow = ({ item, idx, catId }) => {
  const [open, setOpen] = useState(false);
  return (
    <motion.button
      data-testid={`product-${catId}-${idx}`}
      onClick={() => setOpen((v) => !v)}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: idx * 0.05 }}
      className="group text-left py-5 border-b border-[#E6E2D8]"
    >
      <div className="flex items-center justify-between gap-4">
        <span className="font-body font-500 text-lg md:text-xl text-[#2A221C]">{item.name}</span>
        <span className={`shrink-0 text-[#B07D4E] transition-transform duration-300 ${open ? "rotate-45" : ""}`}>
          <Plus className="h-5 w-5" />
        </span>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p
            initial={{ height: 0, opacity: 0, marginTop: 0 }}
            animate={{ height: "auto", opacity: 1, marginTop: 8 }}
            exit={{ height: 0, opacity: 0, marginTop: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden text-[#2A221C]/70 font-light text-sm md:text-base leading-relaxed"
          >
            {item.desc}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.button>
  );
};
